import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import sendMessageIcon from 'assets/icons/send_message.svg';
import lealRoleChatIcon from 'assets/icons/leal_role_chat.png';
import userRoleChatIcon from 'assets/icons/user_role_chat.svg';
import { AssistantMessages } from '../AssistantMessages/AssistantMessages';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import { ChatLoader } from '../ChatLoader/ChatLoader';
import { CHAT_ROLES } from '../constants';
import './genericChat.css';

export const GenericChat = ({
  chatBodyComponent,
  handleSendMessageClick,
  actionPanel,
  chatHistory,
  loader,
  showSpinnerOnly,
  isFullMode,
}) => {
  const isMounted = useIsMounted();
  const [inputMessage, setInputMessage] = useState('');
  const [init, setInit] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const conversationRef = useRef(null);
  const chatPopupRef = useRef(null);

  useEffect(() => {
    setInit(true);

    const handleConversationClick = (event) => {
      const button = event.target.closest('.follow-up-question-button[data-question]');
      if (button && !loading) {
        const question = button.getAttribute('data-question');
        if (question) {
          setInputMessage(question);
          handleSendMessage(question);
        }
      }
    };

    const conversationElement = conversationRef.current;
    if (conversationElement) {
      conversationElement.addEventListener('click', handleConversationClick);
    }

    return () => {
      if (conversationElement) {
        conversationElement.removeEventListener('click', handleConversationClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, handleSendMessageClick]);

  useEffect(() => {
    if (chatPopupRef.current) {
      chatPopupRef.current.scrollTop = chatPopupRef.current.scrollHeight;
    }

    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chatHistory, chatBodyComponent]);

  useEffect(() => {
    if ((loading || loader) && chatPopupRef.current) {
      const popupContentElement = chatPopupRef.current.closest('.popup-content');

      if (popupContentElement) {
        setTimeout(() => {
          popupContentElement.scrollTop = popupContentElement.scrollHeight;
        }, 0);
      } else {
        console.warn("GenericChat: Could not find '.popup-content' to scroll for loading state.");
      }
    }
  }, [loading, loader]);

  const handleSendMessage = useCallback(async (messageToSend) => {
    if (!messageToSend || loading) return;

    setLoading(true);
    setInputMessage('');
    try {
      await handleSendMessageClick(messageToSend);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      if (isMounted()) {
        setLoading(false);
        const textarea = document.getElementById('generic-chat-textarea');
        if (textarea) {
          textarea.focus();
        }
      }
    }
  }, [loading, handleSendMessageClick, isMounted]);

  const handleSendFromInput = useCallback(async () => {
    const messageToActuallySend = inputMessage.trim();
    if (messageToActuallySend) {
      handleSendMessage(messageToActuallySend);
    }
  }, [inputMessage, handleSendMessage]);

  const handleTextareaChange = (event) => {
    setInputMessage(event.target.value);
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  return init ? (
    <div className="chat-popup" ref={chatPopupRef}>
      {showSpinnerOnly ? (
        <div className="chat-popup-sidebar-spinner">
          <ChatLoader />
        </div>
      ) : (
        <>
          {(actionPanel && !isFullMode) ? (actionPanel) : null}
          <div className="chat-actions-panel-divider" />
        </>
      )}

      <div id="conversation" ref={conversationRef} className="chat-popup-conversation">
        {chatHistory?.map((message, index) => {
          if (!message || typeof message?.content === 'undefined' || !message?.role) {
            console.warn('Skipping invalid message object:', message);
            return null;
          }
          return (
            <div key={index} className={`chat-message ${message.role}`}>
              <div className="chat-message-content">
                <div className="chat-role">
                  {message.role !== CHAT_ROLES.ME ? (
                    <img src={lealRoleChatIcon} alt="Leal Role" />
                  ) : (
                    <img src={userRoleChatIcon} alt="User Role" />
                  )}
                </div>
                <div className="chat-message-text">
                  <b>{message.role === CHAT_ROLES.ME ? 'You' : message.role}</b>
                  :
                  {' '}
                  {Array.isArray(message.content) ? (
                    <AssistantMessages messages={message.content} />
                  ) : (
                    <span
                      className="message-html-content"
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {(loading || loader) && <ChatLoader />}
      </div>

      {chatBodyComponent}

      <div className={cx('chat-input', { 'add-absulot': !isFullMode })}>
        {isFullMode && <div style={{ height: '250px' }} />}
        <div className="submit-wrapper">
          <textarea
            id="generic-chat-textarea"
            disabled={loading}
            value={inputMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendFromInput();
              }
            }}
            className="chat-input-textarea"
            maxLength={1500}
            rows={1}
            placeholder="Ask me anything..."
            style={{ maxHeight: '100px' }}
          />
          <button
            type="button"
            id="generic-chat-send-button"
            onClick={handleSendFromInput}
            // Prevent sending if loading or if input is empty/whitespace
            disabled={loading || !inputMessage.trim()}
            className="chat-send-button"
          >
            <img src={sendMessageIcon} alt="Send Message" />
          </button>
        </div>
      </div>
      <div id="bottomRef" ref={bottomRef} style={{ height: '1px' }} />
    </div>
  ) : null;
};

GenericChat.propTypes = {
  chatBodyComponent: PropTypes.any,
  handleSendMessageClick: PropTypes.func.isRequired,
  actionPanel: PropTypes.element,
  chatHistory: PropTypes.arrayOf(PropTypes.shape({
    role: PropTypes.oneOf(Object.values(CHAT_ROLES)).isRequired,
    content: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]).isRequired,
  })),
  loader: PropTypes.bool,
  showSpinnerOnly: PropTypes.bool,
  isFullMode: PropTypes.bool,
};
